
import { Input } from "@/components/ui/input";
import { Search, MapPin, X } from "lucide-react";
import { useState, useEffect } from "react";
import NearMeButton from "./NearMeButton";

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onLocationSearch?: (lat: number, lng: number) => void;
  showNearMe?: boolean;
}

const SearchBar = ({ 
  searchQuery, 
  setSearchQuery, 
  onLocationSearch,
  showNearMe = true 
}: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleLocationFound = (lat: number, lng: number) => {
    if (onLocationSearch) {
      onLocationSearch(lat, lng);
    }
  };

  return (
    <div className={`max-w-xl mx-auto mb-8 relative ${isVisible ? 'animate-slide-in-bottom' : 'opacity-0'}`}>
      <div className={`absolute inset-0 bg-blue-100 rounded-md blur-md transition-all duration-300 ${isFocused ? 'opacity-50 scale-105' : 'opacity-0 scale-100'}`}></div>
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Search by location or owner name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`w-full pl-10 pr-10 py-6 transition-all duration-300 ${isFocused ? 'shadow-lg' : 'shadow'}`}
            />
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 transition-all duration-300 ${isFocused ? 'text-primary-600 animate-pulse' : ''}`} />
            
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {showNearMe && (
            <NearMeButton 
              onLocationFound={handleLocationFound}
              disabled={isFocused}
            />
          )}
        </div>
        
        {isFocused && (
          <div className="absolute mt-2 w-full bg-white rounded-md shadow-lg p-2 z-10 animate-fade-in">
            <div className="p-2 hover:bg-gray-50 rounded flex items-center gap-2 cursor-pointer transition-colors">
              <MapPin className="w-4 h-4 text-blue-500" />
              <span>Current Location</span>
            </div>
            <div className="text-xs text-gray-500 px-2 py-1">Recent Searches</div>
            <div className="p-2 hover:bg-gray-50 rounded flex items-center gap-2 cursor-pointer transition-colors">
              <Search className="w-3 h-3 text-gray-400" />
              <span>Downtown Parking</span>
            </div>
            <div className="p-2 hover:bg-gray-50 rounded flex items-center gap-2 cursor-pointer transition-colors">
              <Search className="w-3 h-3 text-gray-400" />
              <span>Airport Terminal</span>
            </div>
          </div>
        )}
        
        <div className="hidden md:block absolute -right-20 -bottom-10 text-xs bg-primary-100 text-primary-800 p-1 px-3 rounded-full transform rotate-12 animate-float">Try "Near Me" or type an address!</div>
      </div>
    </div>
  );
};

export default SearchBar;
